/**
 * `RuntimeState.session` — the transcript and raw command history
 * (architecture.md §3.2).
 *
 * @remarks
 * `commandHistory` stores player inputs VERBATIM (deliberately unvalidated
 * plain strings — blank and bizarre inputs are real inputs), because
 * `(bundle, seed, commands)` must reproduce the session byte-for-byte
 * (decision D09). Transcript entries are structured; the UI styles by source.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { actorIdSchema, promptIdSchema, promptOptionIdSchema, roomIdSchema } from '../identifiers.js';
import { nonBlankStringSchema, nonNegativeIntegerSchema } from '../primitives.js';
import { runtimeTranscriptEntrySchema } from '../transcript.js';

/**
 * The fields every parked question carries (D79): what was asked (resolved at
 * park time, the R15 pattern — the visible surface ships what the transcript
 * said), which CLAUSE asked it (F5: never the whole input), where it was
 * asked (room-leave is context death, closed loudly), and when (the asking
 * input's ordinal — the question's identity, which the UI's replay-safe
 * dismissal keys on).
 */
/** D179: one side of a confirm — the chip's label and every folded input that means it. */
const confirmAnswerSchema = z
	.strictObject({
		label: nonBlankStringSchema,
		words: z.array(nonBlankStringSchema).min(1).readonly(),
	})
	.readonly();

/**
 * D235: one answer a parked dialogue question OFFERS — the chip's label,
 * which the shell shows and submits, and every folded input that means it
 * (the label and the option's aliases). Resolved at park time, the R15
 * pattern: an option whose `when` failed when the actor asked is not here,
 * so the surface, the record and the matcher read one list.
 */
const dialogueAnswerSchema = z
	.strictObject({
		label: nonBlankStringSchema,
		optionId: promptOptionIdSchema,
		words: z.array(nonBlankStringSchema).min(1).readonly(),
	})
	.readonly();

const pendingQuestionBaseFields = {
	/** The asking input's ordinal in `commandHistory` — the question's identity. */
	askedAtInputOrdinal: nonNegativeIntegerSchema,
	/** The ASKING CLAUSE, folded (F5) — answering resumes here, never the prefix. */
	clauseText: z.string(),
	/** The question as spoken, resolved at park time. */
	questionText: nonBlankStringSchema,
	/** Where the question was asked — leaving this room closes it (D79). */
	roomId: roomIdSchema,
};

/**
 * D247: what a question asked of a CLAUSE carries once a which-one answer has
 * settled part of it — the candidates that answer ruled out. The clause is
 * read without them from then on (the answer's splice, the re-read that asks
 * whether the world has settled it, the in-context refusals), so `use key`,
 * answered `the revival key` and then asked "Use it on what?", still means
 * the revival key when the answer arrives. Plain-string refs (the `candidates`
 * precedent); optional, so every save made before it keeps loading, and
 * absent means nothing was ruled out.
 */
const clauseSettlementFields = {
	ruledOutRefs: z.array(z.string().min(1)).min(1).readonly().optional(),
};

/** D179: what every confirm carries — a yes-or-no gate with its two chips. */
const confirmFields = {
	...pendingQuestionBaseFields,
	/** The chip that keeps things as they are, and the words that mean it. */
	decline: confirmAnswerSchema,
	kind: z.literal('confirm'),
	/** The chip that goes through with it, and the words that mean it. */
	proceed: confirmAnswerSchema,
};

/** The session's structured transcript and verbatim command history. */
export const sessionStateSchema = z
	.strictObject({
		commandHistory: z.array(z.string()).readonly(),
		/**
		 * D235: the conversational partner — the actor the player last
		 * addressed (`talk`, `ask`, `give`, `show`, an answer to its question)
		 * or that last asked the player a prompt. While it is still addressable
		 * from where the player stands, its declared `pronoun` names it ahead of
		 * recency. Never cleared: a partner out of earshot simply stops
		 * applying, and the next body addressed replaces it. Optional, so
		 * every save made before it keeps loading; absent means nobody yet.
		 */
		conversationPartnerId: actorIdSchema.optional(),
		/**
		 * D37 → D79: the question this session is waiting on an answer to — a
		 * discriminated union, because the engine asks two SHAPES of question.
		 * A `completion` is a refusal that asked ("Open it with what?"): a bare
		 * reply splices onto the clause. A `which_entity` is the parser's
		 * clarification ("WHICH?"): the reply names one of the candidates, and
		 * resolution re-parses the clause with the losers removed. Per D79 the
		 * question SURVIVES the player doing other things (visible at the input
		 * line, "she waits"), until answered, replaced by a newer question,
		 * or the player leaves its room. Plain JSON throughout, so saves resume
		 * the conversation and replays re-derive it. D179 adds a third shape, a
		 * `confirm`: a yes-or-no gate before a destructive act (`restart`),
		 * answered by the words it carries and closed — silently — by any
		 * other input, because a gate is not a conversation: the answer is
		 * expected now, and a "yes" typed three commands later must not fire.
		 * D235 adds the fourth, a `dialogue`: an ACTOR's question (`prompts`
		 * on the actor, `docs/design/actors.md` §4), answered by one of the
		 * options it offered — whole-input, never fuzzy — and otherwise
		 * surviving as D79's questions do: an input that matches no option
		 * runs as a command and the prompt waits; leaving the room withdraws
		 * it in the actor's own words.
		 */
		pendingQuestion: z
			.discriminatedUnion('kind', [
				/*
				 * D179 → D212: a confirm is one of two ACTS, and only one of them
				 * carries a corrected clause — so the act discriminates a nested
				 * union rather than an optional field standing in for a rule.
				 */
				z.discriminatedUnion('act', [
					z
						.strictObject({
							...confirmFields,
							/** The destructive `restart` (D179). */
							act: z.literal('restart'),
						})
						.readonly(),
					z
						.strictObject({
							...confirmFields,
							/**
							 * D212: the fuzzy rung read the head verb as a different act
							 * and asked; a "yes" runs `correctedClauseText` as if typed,
							 * any other input closes the question and is read as itself.
							 */
							act: z.literal('run_correction'),
							/** The clause as the rung read it. */
							correctedClauseText: nonBlankStringSchema,
						})
						.readonly(),
				]),
				z
					.strictObject({
						...pendingQuestionBaseFields,
						/** D235: the actor asking — the body whose voice spoke the question, and whose earshot the question lives in. */
						actorId: actorIdSchema,
						kind: z.literal('dialogue'),
						/** The answers offered, in the prompt's declared order — the chips, and the words that answer. */
						options: z.array(dialogueAnswerSchema).min(1).readonly(),
						/** The prompt asked — its `unansweredNarratives` speak if the player walks away. */
						promptId: promptIdSchema,
					})
					.readonly(),
				z
					.strictObject({
						...pendingQuestionBaseFields,
						...clauseSettlementFields,
						/** The word joining answer to clause (`with`, `on`) — empty for a bare direction or verb. */
						joiner: z.string(),
						kind: z.literal('completion'),
						/** True when the answer belongs BEFORE the parked text ("open" + "the kettle"). */
						prefixesAnswer: z.boolean().optional(),
						/**
						 * D225: true when the engine OFFERED this question unasked and
						 * unprinted — D84's bare-noun follow-up, which the surface shows
						 * and the record never carries. A volunteered question closes
						 * silently when its room is left: the one withdrawal line
						 * (`questionWithdrawn`) is for a question the player's own
						 * input created, and a record that never heard the question
						 * must not hear it withdrawn. Optional, so every save made
						 * before it keeps loading; absent means the question was asked.
						 */
						volunteered: z.literal(true).optional(),
					})
					.readonly(),
				z
					.strictObject({
						...pendingQuestionBaseFields,
						...clauseSettlementFields,
						/**
						 * The tied candidates, in the parser's presented order. Refs
						 * are plain strings (the `recentEntityRefs` precedent);
						 * `matchedTokens` is what each candidate matched in the clause.
						 */
						candidates: z
							.array(
								z
									.strictObject({
										entityRef: z.string().min(1),
										matchedTokens: z.array(z.string()).readonly(),
									})
									.readonly(),
							)
							.min(2)
							.readonly(),
						kind: z.literal('which_entity'),
					})
					.readonly(),
			])
			.optional(),
		/*
		 * D67: the pronoun antecedents — entity refs the last executed input
		 * referred to, most recent first, so "take the bar" then "use it" works
		 * ACROSS inputs and not only inside a chain. Session metadata like
		 * `responseUsage`: in state because replays must resolve "it" byte-
		 * identically, defaulted so pre-D67 saves keep loading.
		 */
		recentEntityRefs: z.array(z.string().min(1)).readonly().default([]),
		responseUsage: z.record(z.string(), z.number().int().nonnegative()).readonly().default({}),
		transcript: z.array(runtimeTranscriptEntrySchema).readonly(),
	})
	.readonly();

/** The `session` sub-state — see {@link sessionStateSchema}. */
export type SessionState = z.infer<typeof sessionStateSchema>;
